import { CONCEPT_PAGES } from './content-concepts';
import { FEATURE_PAGES } from './content-features';
import { GUIDE_PAGES } from './content-guide';
import { REFERENCE_PAGES } from './content-reference';
import { SECTIONS, type DocPage, type SectionId } from './types';

/**
 * The documentation registry, plus the search index built from it.
 *
 * Search is a scored substring match over title, summary, keywords and body
 * text. That is deliberately unambitious: the corpus is a few dozen pages, it
 * has to work offline inside the bundle, and a dependency-free matcher that
 * never surprises anyone beats a fuzzy one that occasionally hides the page
 * somebody was looking for.
 */

export const DOC_PAGES: DocPage[] = [
  ...GUIDE_PAGES,
  ...CONCEPT_PAGES,
  ...FEATURE_PAGES,
  ...REFERENCE_PAGES,
];

export const PAGES_BY_SLUG: Record<string, DocPage> = Object.fromEntries(
  DOC_PAGES.map((page) => [page.slug, page]),
);

/** Sections in sidebar order, each carrying its pages in declaration order. */
export function navigation(): { id: SectionId; title: string; pages: DocPage[] }[] {
  return [...SECTIONS]
    .sort((a, b) => a.order - b.order)
    .map((section) => ({
      id: section.id,
      title: section.title,
      pages: DOC_PAGES.filter((page) => page.section === section.id),
    }))
    .filter((section) => section.pages.length > 0);
}

// ---- Search -----------------------------------------------------------------

export interface SearchHit {
  page: DocPage;
  /** The matching line of prose, so a result explains why it matched. */
  excerpt: string | null;
}

/** Flattened body text per page, computed once rather than per keystroke. */
const BODY_TEXT = new Map<string, string[]>(
  DOC_PAGES.map((page) => [
    page.slug,
    page.blocks.flatMap((block): string[] => {
      switch (block.kind) {
        case 'text':
        case 'heading':
        case 'todo':
          return [block.body];
        case 'note':
          return [block.body];
        case 'steps':
        case 'list':
          return block.items;
        case 'table':
          return [block.headers.join(' '), ...block.rows.map((row) => row.join(' '))];
        case 'errors':
          return block.rows.flatMap((row) => [row.code, row.cause, row.fix]);
        case 'code':
          return [block.body];
        case 'screenshot':
          return [block.caption];
      }
    }),
  ]),
);

/**
 * Rank pages against a query.
 *
 * Weighting reflects how people search docs: an exact title is almost always
 * what they meant, a keyword is a strong signal because keywords exist to
 * capture the words that are NOT in the prose, and a body match is a weak one.
 */
export function searchDocs(query: string): SearchHit[] {
  const needle = query.trim().toLowerCase();
  if (needle.length < 2) return [];

  const terms = needle.split(/\s+/).filter(Boolean);

  const scored = DOC_PAGES.map((page) => {
    const title = page.title.toLowerCase();
    const summary = page.summary.toLowerCase();
    const keywords = (page.keywords ?? []).join(' ').toLowerCase();
    const body = BODY_TEXT.get(page.slug) ?? [];

    let score = 0;
    let excerpt: string | null = null;

    for (const term of terms) {
      if (title === term) score += 100;
      else if (title.includes(term)) score += 40;
      if (keywords.includes(term)) score += 20;
      if (summary.includes(term)) score += 10;

      const line = body.find((entry) => entry.toLowerCase().includes(term));
      if (line) {
        score += 4;
        excerpt ??= line;
      }
    }

    // Every term has to appear somewhere, so a two-word query narrows rather
    // than widens — the opposite is what makes a docs search feel useless.
    const haystack = `${title} ${summary} ${keywords} ${body.join(' ')}`.toLowerCase();
    const matchesAll = terms.every((term) => haystack.includes(term));

    return { page, excerpt, score: matchesAll ? score : 0 };
  });

  return scored
    .filter((hit) => hit.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 8)
    .map(({ page, excerpt }) => ({ page, excerpt }));
}

// ---- Self-validation --------------------------------------------------------

/**
 * Invariants the documentation has to hold to be worth trusting.
 *
 * These exist because the docs are meant to expose gaps, and a docs set that
 * can quietly lie about its own completeness is worse than none. Rendered as a
 * banner in development so a violation is caught while writing rather than by a
 * reader months later.
 */
export function validateDocs(): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();

  for (const page of DOC_PAGES) {
    if (seen.has(page.slug)) problems.push(`Duplicate slug: ${page.slug}`);
    seen.add(page.slug);

    if (!page.summary.trim()) problems.push(`${page.slug}: no summary, so search cannot describe it`);

    // The whole point of a non-shipped status is to say what is missing.
    // Claiming "partial" and naming nothing is an empty assertion.
    if (page.status && page.status.state !== 'shipped') {
      if (!page.status.gaps || page.status.gaps.length === 0) {
        problems.push(`${page.slug}: state is "${page.status.state}" but no gaps are listed`);
      }
    }

    // The reverse: a feature cannot be finished while carrying open gaps.
    if (page.status?.state === 'shipped' && (page.status.gaps?.length ?? 0) > 0) {
      problems.push(`${page.slug}: marked shipped but still lists gaps`);
    }

    if (!SECTIONS.some((section) => section.id === page.section)) {
      problems.push(`${page.slug}: unknown section "${page.section}"`);
    }
  }

  return problems;
}
